import { Head } from "@inertiajs/react"

import { HpiStatsBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface UnbilledCase {
  case_id: number
  case_name: string
  estimate: number
}

interface Props {
  cases: UnbilledCase[]
  totals: {
    count: number
    total_estimate: number
  }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDollars(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Unbilled Time & Expenses", href: "/accounting/reports/unbilled" },
]

export default function UnbilledReport({ cases, totals }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Unbilled Time & Expenses" />

      <div className="p-6">
        <HpiStatsBar
          stats={[
            { label: "Total", subLabel: "Cases", value: totals.count },
            {
              label: "Total",
              subLabel: "Estimate",
              value: formatDollars(totals.total_estimate),
            },
          ]}
          className="mb-6"
        />

        <div>
          {/* Blue header bar */}
          <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
            <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
              Unbilled Time & Expenses
            </h3>
          </div>

          {/* Column headers */}
          <div className="border-hpi-light-gray border-b bg-white px-4 py-2">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-hpi-active-blue text-xs font-medium">
                  Case
                </div>
              </div>
              <div className="w-[140px] text-right">
                <div className="text-hpi-active-blue text-xs font-medium">
                  Estimate
                </div>
              </div>
            </div>
          </div>

          {/* Data rows */}
          <div className="bg-white">
            {cases.length === 0 ? (
              <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                No cases with unbilled time or expenses found.
              </div>
            ) : (
              cases.map((row) => (
                <div
                  key={row.case_id}
                  className="border-hpi-light-gray/50 flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <a
                      href={`/cases/${row.case_id}/billing_reports/detail`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue text-sm hover:underline"
                    >
                      {row.case_name}
                    </a>
                  </div>
                  <div className="w-[140px] text-right">
                    <a
                      href={`/cases/${row.case_id}/billing_reports/detail`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue text-sm font-medium hover:underline"
                    >
                      {formatDollars(row.estimate)}
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Totals row */}
          {cases.length > 0 && (
            <div className="border-hpi-light-gray flex items-center gap-3 border-t bg-white px-4 py-3">
              <div className="min-w-0 flex-1 text-sm font-bold">
                {totals.count} Cases
              </div>
              <div className="w-[140px] text-right text-sm font-bold">
                {formatDollars(totals.total_estimate)}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
